import { useEffect, useRef, useState, ReactNode, ElementType } from "react";

type Direction = "up" | "down" | "left" | "right" | "none";

interface FadeInProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  direction?: Direction;
  distance?: number;
  as?: ElementType;
  once?: boolean;
}

const offsetFor = (dir: Direction, dist: number) => {
  switch (dir) {
    case "up": return `translate3d(0, ${dist}px, 0)`;
    case "down": return `translate3d(0, -${dist}px, 0)`;
    case "left": return `translate3d(${dist}px, 0, 0)`;
    case "right": return `translate3d(-${dist}px, 0, 0)`;
    default: return "translate3d(0, 0, 0)";
  }
};

export const FadeIn = ({
  children,
  className = "",
  delay = 0,
  duration = 700,
  direction = "up",
  distance = 24,
  as: Tag = "div",
  once = true,
}: FadeInProps) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref as any}
      className={`will-change-transform ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : offsetFor(direction, distance),
        transition: `opacity ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

export default FadeIn;